import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva } from 'class-variance-authority';
import { cn } from '../../lib/utils';

// Institutional Console redesign (DESIGN-SPEC.md §4.1) button variants.
// Sizing/geometry (h30/r6/px14) and hover states are shared by ALL themes —
// legacy themes still render correctly because every color utility below
// resolves through the existing --color-* bridge (see tailwind.config.js),
// which console-dark.css/console-light.css override additively (issue #231
// pattern) while legacy theme files keep their own values. No variant, size,
// or prop was removed — only default/outline/secondary/ghost/link/success/
// warning color treatments and the base geometry changed; disabled/asChild/
// icon-slot behavior is unchanged.
const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-[12px] font-medium ring-offset-background transition-colors duration-[120ms] ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        // §4.1 Primary: bg --accent, text --on-accent, 700 weight; hover #5fb0ff (dark) / #0d63ae (light).
        // Both hover shades are already the --color-action-primary-bg-hover value in
        // console-dark.css/console-light.css, so bg-primary/90 (opacity trick) is replaced
        // with the exact token-driven hover background.
        default:
          'bg-primary text-primary-foreground font-bold hover:bg-[var(--color-action-primary-bg-hover)]',
        // §4.1 Destructive (rare): 1px rgba(229,72,77,.35) border, text #f0736f; hover bg rgba(229,72,77,.08).
        destructive:
          'border border-[rgba(229,72,77,0.35)] bg-transparent text-[var(--color-error-foreground)] font-medium hover:bg-[rgba(229,72,77,0.08)]',
        // §4.1 Secondary: 1px --border, transparent bg, text --text-secondary, 500 weight;
        // hover bg rgba(255,255,255,0.04) (dark) / #eef2f6 (light), text --text.
        // Round-2 fix (ui-design-expert review N1): previously used
        // --color-action-secondary-bg-hover, which resolves to --surface-hover
        // (0.025 opacity, the TABLE ROW hover value) — under-shooting the
        // spec's button-hover value by 0.015 opacity. `bg-button-hover` binds
        // the correct --button-hover-bg token (added in console-dark.css/
        // console-light.css) instead.
        outline:
          'border border-[var(--color-action-secondary-border)] bg-transparent text-[var(--color-action-secondary-text)] hover:bg-button-hover hover:text-foreground',
        secondary:
          'bg-secondary text-secondary-foreground hover:bg-[var(--color-surface-hover)]',
        ghost: 'hover:bg-accent hover:text-accent-foreground',
        link: 'text-primary underline-offset-4 hover:underline',
        success: 'bg-success text-success-foreground hover:bg-success/90',
        warning: 'bg-warning text-warning-foreground hover:bg-warning/90',
      },
      size: {
        // §4.1/§2.4: control height 30px, radius 6px, horizontal padding 14px.
        default: 'h-[30px] rounded-md px-3.5 py-0',
        sm: 'h-8 rounded-md px-3',
        lg: 'h-11 rounded-md px-8',
        icon: 'h-[30px] w-[30px]',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

/**
 * Button component (shadcn/ui)
 *
 * A versatile button component with multiple variants and sizes, restyled
 * for the Institutional Console redesign (DESIGN-SPEC.md §4.1): 30px height,
 * 6px radius, 14px horizontal padding, and the primary/secondary/destructive
 * color treatments. Uses Tailwind CSS for styling via class-variance-authority.
 *
 * @param {Object} props
 * @param {'default'|'destructive'|'outline'|'secondary'|'ghost'|'link'|'success'|'warning'} props.variant
 * @param {'default'|'sm'|'lg'|'icon'} props.size
 * @param {boolean} props.asChild - Render as Slot for custom elements
 * @param {string} props.className - Additional classes
 */
const Button = React.forwardRef(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  }
);
Button.displayName = 'Button';

/**
 * RowIconAction - §4.1 "Row icon action" button variant.
 *
 * Icon-only button used for always-visible per-row actions in tables (edit/
 * sell/delete, etc). 13px icon, --text-faint default color, 4px padding, 4px
 * radius; hover applies an intent color + matching 10% tint background
 * (edit -> text color, sell -> positive, delete -> negative). Deliberately a
 * separate component rather than a `buttonVariants` size/variant combo
 * because the intent-based hover color can't be expressed as a single static
 * class list — see Table §4.3 "row actions... visible (do not hide until
 * hover)".
 *
 * @param {Object} props
 * @param {'neutral'|'positive'|'negative'} props.intent - hover tint (default 'neutral' uses --text)
 * @param {string} props.label - accessible name (required; renders aria-label + title)
 * @param {React.ReactNode} props.children - icon element
 */
const RowIconAction = React.forwardRef(
  ({ className, intent = 'neutral', label, children, ...props }, ref) => {
    const intentHoverClasses = {
      neutral: 'hover:text-foreground hover:bg-[rgba(255,255,255,0.06)]',
      positive: 'hover:text-success hover:bg-[var(--color-state-success-bg)]',
      negative: 'hover:text-destructive hover:bg-[var(--color-state-error-bg)]',
    };

    return (
      <button
        ref={ref}
        type="button"
        aria-label={label}
        title={label}
        className={cn(
          'inline-flex items-center justify-center rounded-sm p-1 text-[var(--text-faint,var(--color-text-disabled))] transition-colors duration-[120ms] ease-out',
          'min-h-[44px] min-w-[44px] sm:min-h-0 sm:min-w-0', // 44px touch target on mobile (§7), collapses to 4px padding on larger pointers
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2',
          '[&_svg]:size-[13px] [&_svg]:pointer-events-none [&_svg]:shrink-0',
          intentHoverClasses[intent] || intentHoverClasses.neutral,
          className
        )}
        {...props}
      >
        {children}
      </button>
    );
  }
);
RowIconAction.displayName = 'RowIconAction';

export { Button, buttonVariants, RowIconAction };
